import {
    Component,
    OnInit,
    inject,
    DestroyRef,
    ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    ButtonColor,
    ButtonSize,
    GridComponent,
    TapestrySidenavService
} from '@predictiveindex/tapestry';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
    ClassificationType,
    SellerToolsAccountTableData,
    ValidationResult
} from '../../../../core-data-model';
import { DemoAccountService } from '../../../../core-services';
import { FeatureFlags, FeatureFlagsService } from '../../../../system-services';
import { TranslationService } from '../../../../translations';
import { AddThirdPartyUsersModalComponent } from '../components/add-third-party-users-modal/add-third-party-users-modal.component';
import { DeleteAccountModalComponent } from '../components/delete-account-modal/delete-account-modal.component';
import { ActionMenuComponent } from './action-menu/action-menu.component';

type CreateWorkshopAccountForm = FormGroup<{
    workshopName: FormControl<string>;
}>;

@Component({
    selector: 'workshop-accounts',
    templateUrl: './workshop-accounts.component.html',
    styleUrls: ['../seller-tools-base.component.less'],
    standalone: false
})
export class WorkshopAccountsComponent implements OnInit {
    @ViewChild('workshopAccountsGrid') workshopAccountsGrid: GridComponent;

    private demoAccount = inject(DemoAccountService);
    private TR = inject(TranslationService);
    private fb = inject(FormBuilder);
    private dialog = inject(MatDialog);
    private flags = inject(FeatureFlagsService);
    private tapestrySidenav = inject(TapestrySidenavService);
    private destroyRef = inject(DestroyRef);

    showWorkshopCreateField = false;
    columnDefs: ColDef[];
    workshopForm: CreateWorkshopAccountForm;
    saving = false;
    workshopAccountData$ = this.demoAccount.workshopAccounts$;

    protected readonly ButtonSize = ButtonSize;
    protected readonly ButtonColor = ButtonColor;

    ngOnInit(): void {
        this.workshopForm = this.fb.group({
            workshopName: this.fb.control('', [Validators.required])
        });
        this.columnDefs = this.getColumnDefs();
        this.demoAccount
            .getWorkshopAccounts()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((accounts) => {
                this.demoAccount.setWorkshopAccounts(accounts);
            });
    }

    gridReadyHandler(params: GridReadyEvent) {
        this.workshopAccountsGrid.gridApi = params.api;
        this.registerParentContext();
    }

    getColumnDefs(): ColDef[] {
        return [
            ...(this.flags.getByFlag(FeatureFlags.enableSellerToolsDelete)
                ? [
                      {
                          headerName: '',
                          checkboxSelection: true,
                          headerCheckboxSelection: true,
                          width: 50
                      }
                  ]
                : []),
            {
                headerName: 'Workshop Account',
                field: 'accountName',
                sortable: true,
                suppressMovable: true,
                cellRenderer: null,
                flex: 1
            },
            {
                headerName: this.TR.key(
                    'seller-tools-panel.tab-table.active-col'
                ),
                field: 'lastActive',
                sortable: true,
                suppressMovable: true,
                width: 150,
                cellRenderer: null,
                comparator: (valueA, valueB) =>
                    this.demoAccount.lastActiveComparator(valueA, valueB)
            },
            {
                headerName: '',
                width: 50,
                cellStyle: {
                    paddingLeft: '0',
                    paddingRight: '0'
                },
                cellRenderer: ActionMenuComponent,
                suppressMovable: true
            }
        ];
    }

    showAddWorkshopAccount() {
        this.showWorkshopCreateField = true;
    }

    cancel() {
        this.showWorkshopCreateField = false;
        this.saving = false;
        this.resetForm();
    }

    saveChanges() {
        this.saving = true;
        const formattedWorkshopName = this.workshopForm.value.workshopName;

        this.demoAccount
            .create(formattedWorkshopName, [], ClassificationType.Workshop)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.saving = false;
                if (
                    response &&
                    response.validationResult ===
                        ValidationResult.DuplicateCompanyName
                ) {
                    this.workshopForm.controls.workshopName.setErrors({
                        duplicateProspectName: true
                    });
                }

                if (
                    response &&
                    response.statusCode >= 200 &&
                    response.statusCode < 400
                ) {
                    this.showWorkshopCreateField = false;
                    this.resetForm();
                }
            });
    }

    private addThirdPartyUsers(accountInfo: SellerToolsAccountTableData) {
        this.tapestrySidenav.close();
        this.dialog.open(AddThirdPartyUsersModalComponent, {
            width: '580px',
            backdropClass: 'tapestry--overlay',
            restoreFocus: true,
            closeOnNavigation: true,
            data: accountInfo,
            exitAnimationDuration: 200,
            disableClose: true
        });
    }

    private deleteAccountConfirmation(
        accountInfo: SellerToolsAccountTableData
    ) {
        this.tapestrySidenav.close();
        this.dialog.open(DeleteAccountModalComponent, {
            width: '580px',
            backdropClass: 'tapestry--overlay',
            restoreFocus: true,
            closeOnNavigation: false,
            data: accountInfo,
            exitAnimationDuration: 200
        });
    }

    private registerParentContext() {
        if (this.workshopAccountsGrid.gridApi) {
            this.workshopAccountsGrid.gridApi.setGridOption('context', {
                deleteAccountConfirmation:
                    this.deleteAccountConfirmation.bind(this),
                addThirdPartyUsers: this.addThirdPartyUsers.bind(this)
            });
        }
    }

    private resetForm() {
        this.workshopForm.reset();
        this.workshopForm.markAsUntouched();
        this.workshopForm.markAsPristine();
        this.workshopForm.updateValueAndValidity();
    }
}
